import React from 'react';
import { motion } from 'framer-motion';
import { Award, ChevronRight, Coins, Sparkles, Trophy } from 'lucide-react';
import { levelName } from '../constants/gameplay';

function LevelProgressionCard({ progression, language = 'en' }) {
  if (!progression) return null;

  const {
    current_level_num,
    current_level_stages_completed = 0,
    stages_required = 5,
    overall_percent,
    can_advance,
    is_max_level,
    next_level_num,
    next_level_rewards,
  } = progression;

  const lang = language === 'zh' ? 'zh' : 'en';
  const currentName = levelName(current_level_num, lang);
  const nextName = next_level_num ? levelName(next_level_num, lang) : null;
  const completed = Math.min(current_level_stages_completed, stages_required);
  const percent = Number.isFinite(overall_percent)
    ? overall_percent
    : Math.round((completed / stages_required) * 100);

  return (
    <div className="overflow-hidden rounded-2xl border-2 border-zinc-200 bg-white" data-testid="level-progression-card">
      <div className="bg-gradient-to-r from-violet-500 via-pink-500 to-orange-500 p-5 text-white">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <CircularProgress percent={percent} />
            <div>
              <p className="text-xs font-bold uppercase opacity-90">
                {lang === 'zh' ? '\u5f53\u524d\u5173\u5361' : 'Current Level'}
              </p>
              <h2 className="text-2xl font-black" data-testid="current-level-name">{currentName}</h2>
              <p className="text-sm opacity-90">
                {completed}/{stages_required} {lang === 'zh' ? '\u9636\u6bb5\u5df2\u5b8c\u6210' : 'stages completed'}
              </p>
            </div>
          </div>

          <div className="rounded-xl bg-white/15 px-3 py-2 text-right text-sm font-bold">
            {can_advance
              ? (lang === 'zh' ? '\u4e0b\u4e00\u5173\u5df2\u89e3\u9501' : 'Next level unlocked')
              : (lang === 'zh' ? '\u5b8c\u6210 3 \u4e2a\u9636\u6bb5\u89e3\u9501' : 'Clear 3 stages to unlock')}
          </div>
        </div>
      </div>

      <div className="p-5">
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="font-bold text-zinc-700">
            {lang === 'zh' ? '\u5173\u5361\u8fdb\u5ea6' : 'Level Progress'}
          </span>
          <span className="font-black text-zinc-900">{Math.round(percent)}%</span>
        </div>
        <div className="h-3 overflow-hidden rounded-full bg-zinc-100">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, percent)}%` }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="h-full rounded-full bg-gradient-to-r from-violet-500 to-pink-500"
          />
        </div>
      </div>

      {!is_max_level && nextName && (
        <div className="border-t-2 border-zinc-100 bg-gradient-to-br from-zinc-50 to-violet-50 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase text-zinc-500">
                {lang === 'zh' ? '\u4e0b\u4e00\u5173' : 'Next Level'}
              </p>
              <div className="mt-1 flex items-center gap-2">
                <ChevronRight className="h-5 w-5 text-violet-500" />
                <h3 className="text-xl font-black text-zinc-900" data-testid="next-level-name">{nextName}</h3>
              </div>
            </div>
            {next_level_rewards && (
              <div className="flex flex-wrap gap-2" data-testid="next-level-rewards">
                <RewardChip
                  icon={Coins}
                  label={`${next_level_rewards.coins} ${lang === 'zh' ? '\u91d1\u5e01' : 'Coins'}`}
                  color="amber"
                />
                <RewardChip icon={Sparkles} label={`${next_level_rewards.xp} XP`} color="violet" />
                {next_level_rewards.badge && (
                  <RewardChip
                    icon={Award}
                    label={`${next_level_rewards.badge} ${lang === 'zh' ? '\u5fbd\u7ae0' : 'Badge'}`}
                    color="pink"
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {is_max_level && (
        <div className="border-t-2 border-zinc-100 bg-gradient-to-br from-yellow-50 to-orange-50 p-5 text-center">
          <Trophy className="mx-auto mb-1 h-8 w-8 text-yellow-500" />
          <p className="font-black text-zinc-900">
            {lang === 'zh' ? '\u5df2\u8fbe\u6700\u9ad8\u5173\u5361!' : 'Max Level Achieved!'}
          </p>
        </div>
      )}
    </div>
  );
}

function RewardChip({ icon: Icon, label, color = 'violet' }) {
  const colors = {
    amber: 'bg-amber-100 text-amber-700',
    violet: 'bg-violet-100 text-violet-700',
    pink: 'bg-pink-100 text-pink-700',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-bold ${colors[color]}`}>
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}

function CircularProgress({ percent = 0 }) {
  const size = 60;
  const stroke = 6;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(100, percent) / 100) * circumference;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255,255,255,0.25)"
          strokeWidth={stroke}
          fill="none"
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="white"
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          strokeDasharray={circumference}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-xs font-black">
        {Math.round(percent)}%
      </div>
    </div>
  );
}

export default LevelProgressionCard;
